import React from 'react';
import { CheckCircle2, Download, Loader2, TriangleAlert } from 'lucide-react';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';

/**
 * What a massive upload is doing, and what it ended up doing.
 *
 * Presentational: the state arrives through props, because the file is created
 * by a worker and the screen learns about it through the socket. The panel in
 * the header tells the same story in one line — this is the detail, and the
 * only place the failed rows are readable.
 *
 * @param {object} props
 * @param {'sending'|'running'|'stalled'|'done'} props.phase
 * @param {number} props.total
 * @param {number} props.processed
 * @param {number} props.created
 * @param {number} props.failed
 * @param {Array<{fila: number, id_referencia: string, motivo: string}>} props.errors
 * @param {string|null} props.errorsUrl
 * @param {string} props.message
 * @param {number} [props.sent] - Bytes of the file sent, while it goes up.
 * @param {number} [props.size] - The file's size, once the browser knows it.
 * @param {() => void} props.onReset
 * @param {() => void} props.onGoToDeliveries
 */
export const OrderUploadProgress = ({
  phase,
  total = 0,
  processed = 0,
  created = 0,
  failed = 0,
  errors = [],
  errorsUrl = null,
  message = '',
  sent = 0,
  size = 0,
  onReset,
  onGoToDeliveries,
}) => {
  const isDone = phase === 'done';
  const isStalled = phase === 'stalled';
  // Before the first progress event there is nothing to divide by, and a bar
  // stuck at zero reads as broken — so it only appears once there is a total.
  const percentage = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : null;
  const sentPercentage = size > 0 ? Math.min(100, Math.round((sent / size) * 100)) : null;

  return (
    <div
      data-testid="order-upload-progress"
      className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 sm:p-8 shadow-sm border border-border space-y-6 animate-in fade-in duration-300"
    >
      <div className="flex items-start gap-3">
        {isStalled || (isDone && failed > 0) ? (
          <TriangleAlert size={20} className="text-warning mt-0.5 shrink-0" />
        ) : isDone ? (
          <CheckCircle2 size={20} className="text-ninja-green mt-0.5 shrink-0" />
        ) : (
          <Loader2 size={20} className="text-ninja-green animate-spin mt-0.5 shrink-0" />
        )}
        <div>
          <h3 className="text-lg font-bold text-ninja-dark dark:text-white">
            {isStalled
              ? 'Sin novedades del proceso'
              : isDone
                ? failed > 0
                  ? 'Carga terminada con errores'
                  : 'Carga terminada'
                : 'Creando las órdenes'}
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            {isStalled
              ? 'Dejamos de recibir avances. Puede que el proceso siga corriendo, así que revisá el historial de envíos antes de volver a subir el archivo: si las órdenes ya están, subirlo otra vez las duplica.'
              : message || 'Esto puede tomar unos minutos. Podés seguir usando la plataforma.'}
          </p>
        </div>
      </div>

      {isStalled && processed > 0 && (
        <p className="text-sm text-text-muted" data-testid="order-upload-stalled-progress">
          Lo último que supimos: {processed} de {total} filas procesadas.
        </p>
      )}

      {/* The file going up comes before any row: a big one takes a while, and
          nothing else on screen moves meanwhile. */}
      {phase === 'sending' && sentPercentage !== null && (
        <ProgressBar
          value={sent}
          max={size}
          label={`Subiendo el archivo: ${sentPercentage}%`}
          testId="order-upload-send-progress"
        />
      )}

      {!isDone && !isStalled && percentage !== null && (
        <ProgressBar value={processed} max={total} label={`${processed} de ${total}`} />
      )}

      {isDone && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Datum label="Filas del archivo" value={total} />
          <Datum label="Órdenes creadas" value={created} tone="text-ninja-green" />
          <Datum
            label="No se cargaron"
            value={failed}
            tone={failed ? 'text-danger' : undefined}
            highlighted={failed > 0}
          />
        </div>
      )}

      {/*
        The reason the card exists. It used to hang the download off a grey link
        beside a heading, which put the one action that fixes anything where it
        read as the least important thing on screen.
      */}
      {isDone && failed > 0 && (
        <div
          data-testid="order-upload-fix-banner"
          className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5 rounded-2xl border border-warning/30 bg-warning/5"
        >
          <div className="flex-1">
            <h4 className="text-base font-bold text-ninja-dark dark:text-white">
              {failed === 1
                ? 'Quedó 1 orden sin crear'
                : `Quedaron ${failed} órdenes sin crear`}
            </h4>
            <p className="text-sm text-text-secondary mt-1">
              Descargar el archivo: trae esas filas con todos sus datos y una columna{' '}
              <span className="font-semibold">Motivo</span> al final. Corrige lo que indica y
              vuelve a subir ese mismo archivo.
            </p>
          </div>
          {errorsUrl && (
            /* An anchor and not `Button`, which renders a <button>: this has to
               be a real link for the browser to download it. The classes are
               the ones the primary variant uses. */
            <a
              href={errorsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-upload-errors-download"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm shadow-sm transition-colors whitespace-nowrap"
            >
              <Download size={16} />
              {failed === 1 ? 'Descargar la fila' : `Descargar las ${failed} filas`}
            </a>
          )}
        </div>
      )}

      {isDone && errors.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-bold text-ninja-dark dark:text-white">
            Detalle de lo que no se cargó
          </h4>

          {/* The payload is capped, so with many failures the table is showing
              a subset and has to say so — the file always has them all. */}
          {failed > errors.length && (
            <p className="text-sm text-text-muted" data-testid="order-upload-errors-capped">
              Mostramos las primeras {errors.length} de {failed}. El archivo las trae todas.
            </p>
          )}

          <div className="overflow-x-auto border border-border rounded-xl">
            <table className="w-full text-sm" data-testid="order-upload-errors-table">
              <thead className="bg-ninja-gray/60 dark:bg-[#2d2d2d]">
                <tr className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider">
                  <th className="px-4 py-2.5">Fila</th>
                  <th className="px-4 py-2.5">Id de referencia</th>
                  <th className="px-4 py-2.5">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((error) => (
                  <tr key={`${error.fila}-${error.id_referencia}`} className="border-t border-border">
                    <td className="px-4 py-2.5 text-text-secondary">{error.fila}</td>
                    <td className="px-4 py-2.5 text-ninja-dark dark:text-white font-medium">
                      {error.id_referencia || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary">{error.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(isDone || isStalled) && (
        <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2 border-t border-border">
          <Button variant="subtle" pill onClick={onReset}>
            Cargar otro archivo
          </Button>
          {/* The primary action when nothing is being heard is to go and look,
              not to try again. */}
          <Button variant="primary" pill onClick={onGoToDeliveries}>
            Ver el historial de envíos
          </Button>
        </div>
      )}
    </div>
  );
};

/** `highlighted` is for the count that asks for something to be done. */
const Datum = ({ label, value, tone, highlighted = false }) => (
  <div className={highlighted ? 'space-y-1 rounded-xl bg-danger/5 border border-danger/20 px-4 py-3' : 'space-y-1'}>
    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</span>
    <p className={`text-2xl font-bold ${tone || 'text-ninja-dark dark:text-white'}`}>{value}</p>
  </div>
);
